import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { 
  uploadProfileImageWithSignedUrl, 
  shouldUseSignedUrlUpload, 
  validateProfileImage 
} from '../utils/profileImageUpload';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Check token and get user data
  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const res = await axios.get(`${API_URL}/auth/profile`);
        setUser(res.data);
      }
    } catch (err) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    }
    
    setLoading(false);
  };

  // Register user
  const register = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      const res = await axios.post(`${API_URL}/auth/register`, userData);
      
      // Note: No token is provided until email is verified
      // Don't set token or user in localStorage
      return res.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login user
  const login = async (email, password, accountType = null) => {
    try {
      setError(null);
      setLoading(true);
      const res = await axios.post(`${API_URL}/auth/login`, { email, password, accountType });
      
      // Check if multiple accounts exist and user needs to choose
      if (res.data.multipleAccounts) {
        return res.data; // Return the accounts data for selection
      }
      
      localStorage.setItem('token', res.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      setUser(res.data);
      return res.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setError(null);
  };

  // Update user profile
  const updateProfile = async (profileData, profileImage = null, onProgress = null) => {
    try {
      setError(null);
      setLoading(true);
      
      // If there's a profile image, decide which upload method to use
      if (profileImage) {
        // Validate the image first
        const validation = validateProfileImage(profileImage);
        if (!validation.isValid) {
          throw new Error(validation.errors.join(', '));
        }

        // Check if we should use signed URL upload (for large files)
        if (shouldUseSignedUrlUpload(profileImage)) {
          try {
            // Use signed URL upload for large images
            onProgress && onProgress(0, 'Starting upload...');
            
            const uploadResult = await uploadProfileImageWithSignedUrl(
              profileImage, 
              localStorage.getItem('token'),
              (progress, status) => {
                onProgress && onProgress(progress, status || 'Uploading...');
              }
            );
            
            // Update local user state with the new data
            setUser(uploadResult.user);
            return uploadResult.user;
            
          } catch (signedUrlError) {
            // If signed URL upload fails due to service unavailability, fall back to regular upload
            if (signedUrlError.message.includes('SIGNED_URLS_UNAVAILABLE')) {
              // Continue to regular upload below
            } else {
              throw signedUrlError;
            }
          }
        }
      }
      
      // Regular upload (for small images or when signed URLs are unavailable)
      onProgress && onProgress(20, 'Preparing upload...');
      
      // Create FormData for regular upload
      const formData = new FormData();
      
      // Add profile data
      Object.keys(profileData).forEach(key => {
        if (key === 'socialLinks') {
          // Handle nested socialLinks object
          Object.keys(profileData[key]).forEach(socialKey => {
            formData.append(`socialLinks[${socialKey}]`, profileData[key][socialKey] || '');
          });
        } else if (Array.isArray(profileData[key])) {
          // Handle arrays (like genres)
          profileData[key].forEach(item => {
            formData.append(key, item);
          });
        } else {
          formData.append(key, profileData[key]);
        }
      });
      
      // Add profile image if provided
      if (profileImage) {
        formData.append('profileImage', profileImage);
        onProgress && onProgress(40, 'Uploading image...');
      } else {
        onProgress && onProgress(60, 'Updating profile...');
      }
      
      const res = await axios.put(`${API_URL}/auth/profile`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (profileImage && onProgress) {
            const uploadProgress = Math.round((progressEvent.loaded * 40) / progressEvent.total) + 40;
            onProgress(Math.min(uploadProgress, 90), 'Uploading...');
          }
        }
      });
      
      onProgress && onProgress(100, 'Complete!');
      setUser(res.data);
      return res.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update profile';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Verify email with token
  const verifyEmail = async (queryParams) => {
    try {
      setError(null);
      setLoading(true);
      const res = await axios.get(`${API_URL}/auth/verify-email?${queryParams}`);
      return res.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Email verification failed';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Resend verification email
  const resendVerificationEmail = async (email, accountType = null) => {
    try {
      setError(null);
      setLoading(true);
      const res = await axios.post(`${API_URL}/auth/resend-verification`, { email, accountType });
      return res.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to resend verification email';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        setError,
        register,
        login,
        logout,
        updateProfile,
        verifyEmail,
        resendVerificationEmail
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;